import { authoringOpenings, buildAuthoringGrid } from "../compile/collision.ts";
import type { CompileContext } from "../compile/context.ts";
import { blockingFootprint, laysPassages } from "../compile/prop-footprint.ts";
import { EMPTY_TILE } from "../document/packed-tile.ts";
import { COLLISION_CELLS_PER_TILE, type LayerDocument, type MapDocument, type PropInstance, type WorldDocument } from "../document/types.ts";

/**
 * What makes one collision cell block (ADR-045 amendment of 2026-09-24): the editor names it when an erase
 * in Collision mode removes nothing, since only painted collision can be erased there and a prop's
 * footprint, a tile's footprint or a terrain rule is edited elsewhere. Props and tiles are tested on their
 * own through `buildCollisionGrid`, so the answer follows exactly the stamping the overlay and the
 * compiler do. A cell a collision layer opens with the game's open mask (ADR-017's and ADR-045's
 * amendments of 2026-10-04) is named too: opened when nothing blocks it, and on the source that still blocks
 * it otherwise. So is a cell a placed prop opens with its footprint's passage (ADR-017's amendment of
 * 2026-10-05).
 */

/**
 * Where a blocking cell's collision comes from, in the order it is looked for: blocking paint on a collision
 * layer (the topmost layer holding a bit outside the game's open mask), a placed prop's footprint, initial
 * state or tiles-rule anchor (the topmost prop), a painted tile's footprint or tiles rule (the tile on the
 * cell's own tile, topmost layer first), else the game's rules over the terrain. `opened` on a blocking
 * source is the topmost collision layer whose open paint lies under it, where the terrain's rules write
 * nothing; a cell with open paint that nothing blocks is `opened`, by that layer. `openedBy` on a blocking
 * source is the topmost prop whose passage lies under it; a cell a prop's passage opens, that no paint opens and
 * nothing blocks, is `opened` by that prop's layer with its `propId`. Paint is named before a prop, since an
 * erase in Collision mode can reach it.
 */
export type CollisionSource =
  | { readonly kind: "painted"; readonly layerId: string; readonly opened?: string; readonly openedBy?: PropOpener }
  | { readonly kind: "prop"; readonly layerId: string; readonly propId: string; readonly opened?: string; readonly openedBy?: PropOpener }
  | { readonly kind: "tile"; readonly layerId: string; readonly x: number; readonly y: number; readonly opened?: string; readonly openedBy?: PropOpener }
  | { readonly kind: "rule" }
  | { readonly kind: "opened"; readonly layerId: string; readonly propId?: string };

/**
 * A placed prop whose footprint's passage opens a cell: its layer and its id.
 */
export interface PropOpener {
  readonly layerId: string;
  readonly propId: string;
}

/**
 * Finds what makes a collision cell block, or what opens it.
 *
 * @param world The world, for its tile size and tileset order.
 * @param map The map.
 * @param context The compile context, for the rules, the footprints and the open mask.
 * @param index The cell's index in the map's collision grid.
 * @param effective The map's effective collision grid, read only when no layer, prop or tile explains
 *   the cell.
 * @param states The states the overlay shows behaviour props in, by prop id (P1.6-T74).
 * @returns The source, or null when the cell neither blocks nor is opened.
 * @throws RangeError when the index lies outside the collision grid.
 */
export function collisionSourceAt(world: WorldDocument, map: MapDocument, context: CompileContext, index: number, effective: () => Uint8Array, states: ReadonlyMap<string, string> = new Map()): CollisionSource | null {
  const gridWidth = map.width * COLLISION_CELLS_PER_TILE;
  const size = gridWidth * map.height * COLLISION_CELLS_PER_TILE;
  if (!Number.isInteger(index) || index < 0 || index >= size) {
    throw new RangeError(`collision cell ${index} is outside [0, ${size})`);
  }
  const tx = Math.floor((index % gridWidth) / COLLISION_CELLS_PER_TILE);
  const ty = Math.floor(Math.floor(index / gridWidth) / COLLISION_CELLS_PER_TILE);
  const layers = [...map.layers].reverse();
  const open = context.collision.open ?? 0;
  const opener = open === 0 ? undefined : layers.find((layer) => layer.kind === "collision" && ((layer.cells[index] ?? 0) & open) !== 0);
  const deck = propOpener(world, map, context, layers, index, tx, ty);
  const over = { ...(opener === undefined ? {} : { opened: opener.id }), ...(deck === null ? {} : { openedBy: deck }) };
  const painted = layers.find((layer) => layer.kind === "collision" && ((layer.cells[index] ?? 0) & ~open) !== 0);
  if (painted !== undefined) {
    return { kind: "painted", layerId: painted.id, ...over };
  }
  for (const layer of layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    const prop = [...layer.props].reverse().find((p) => reaches(p, context, world.tileSize, tx, ty) && blocksAlone(world, map, context, { ...layer, props: [p] }, index, states));
    if (prop !== undefined) {
      return { kind: "prop", layerId: layer.id, propId: prop.id, ...over };
    }
  }
  const at = ty * map.width + tx;
  for (const layer of layers) {
    const packed = layer.kind === "tile" ? (layer.tiles[at] ?? EMPTY_TILE) : EMPTY_TILE;
    if (layer.kind === "tile" && packed !== EMPTY_TILE) {
      const tiles = new Uint32Array(layer.tiles.length).fill(EMPTY_TILE);
      tiles[at] = packed;
      if (blocksAlone(world, map, context, { ...layer, tiles }, index, states)) {
        return { kind: "tile", layerId: layer.id, x: tx, y: ty, ...over };
      }
    }
  }
  if (opener !== undefined) {
    return { kind: "opened", layerId: opener.id };
  }
  if (deck !== null) {
    return { kind: "opened", layerId: deck.layerId, propId: deck.propId };
  }
  return (effective()[index] ?? 0) !== 0 ? { kind: "rule" } : null;
}

/**
 * The topmost placed prop whose footprint's passage opens a cell, tested prop by prop through `propOpenings`,
 * so the answer follows exactly what the compile opens.
 *
 * @param world The world.
 * @param map The map, for its size.
 * @param context The compile context.
 * @param layers The map's layers, topmost first.
 * @param index The cell.
 * @param tx The cell's tile column.
 * @param ty The cell's tile row.
 * @returns The prop, or null when none opens the cell.
 */
function propOpener(world: WorldDocument, map: MapDocument, context: CompileContext, layers: readonly LayerDocument[], index: number, tx: number, ty: number): PropOpener | null {
  if (!laysPassages(context.collision)) {
    return null;
  }
  for (const layer of layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    const prop = [...layer.props].reverse().find((p) => reaches(p, context, world.tileSize, tx, ty) && (authoringOpenings(world, { ...map, layers: [{ ...layer, props: [p] }] }, context)?.[index] ?? 0) !== 0);
    if (prop !== undefined) {
      return { layerId: layer.id, propId: prop.id };
    }
  }
  return null;
}

/**
 * Whether a prop's collision could reach a tile: the box of the footprint it blocks by (`blockingFootprint`), which
 * also bounds what a behaviour prop's initial state stamps, and at least its anchor tile, which a tiles rule
 * marks.
 *
 * @param prop The prop.
 * @param context The compile context.
 * @param tileSize Tile size in pixels.
 * @param tx Tile column.
 * @param ty Tile row.
 * @returns True when the tile lies inside that box.
 */
function reaches(prop: PropInstance, context: CompileContext, tileSize: number, tx: number, ty: number): boolean {
  const footprint = blockingFootprint(prop, context, tileSize);
  const w = Math.max(tileSize, footprint?.w ?? 0) / tileSize;
  const h = Math.max(tileSize, footprint?.h ?? 0) / tileSize;
  return tx >= Math.floor(prop.x) && tx < Math.ceil(prop.x + w) && ty >= Math.floor(prop.y + 1 - h) && ty < Math.ceil(prop.y + 1);
}

/**
 * Whether one layer on its own blocks a cell under the game's rules.
 *
 * @param world The world.
 * @param map The map, for its size.
 * @param context The compile context.
 * @param layer A prop or tile layer holding only what is being tested.
 * @param index The cell.
 * @param states The states the overlay shows behaviour props in, by prop id.
 * @returns True when the cell blocks.
 */
function blocksAlone(world: WorldDocument, map: MapDocument, context: CompileContext, layer: LayerDocument, index: number, states: ReadonlyMap<string, string>): boolean {
  return (buildAuthoringGrid(world, { ...map, layers: [layer] }, { layers: [] }, context, { states })[index] ?? 0) !== 0;
}
